import React, { useContext, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import UsersContext from "../Context/UsersContext";
import FarmerMgmt from "../Farmer/FarmerMgmt";
import FarmerFinish from "../Farmer/FarmerFinish";
import MoneyTransfer from "../Components/MoneyTransfer";
import ManagePayments from "../Payments/ManagePayments";
import NearComplete from "../Farmer/NearComplete";

// ── Page components ──────────────────────────────────────────────────────────
const FarmerEdit = () => {
  const { farmers } = useContext(UsersContext);
  return (
    <div className="mt-4">
      <FarmerMgmt users={farmers} />
    </div>
  );
};

const FarmerFninsh = () => {
  const { farmers } = useContext(UsersContext);
  return (
    <div>
      <FarmerFinish users={farmers} />
    </div>
  );
};

const NearCompete = () => {
  const { farmers } = useContext(UsersContext);
  return (
    <div>
      <NearComplete users={farmers} />
    </div>
  );
};

const MoneyTransf = () => (
  <div>
    <MoneyTransfer />
  </div>
);

const PaymentSettings = () => (
  <div>
    <ManagePayments />
  </div>
);

// ── SVG Icons ────────────────────────────────────────────────────────────────
const icons = {
  EditInvestor: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-5 h-5"
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  NearComplete: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-5 h-5"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  ),
  CompleteInvestment: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-5 h-5"
    >
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
  ManageInvestment: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-5 h-5"
    >
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <line x1="2" y1="10" x2="22" y2="10" />
    </svg>
  ),
  TransferInvestment: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-5 h-5"
    >
      <polyline points="17 1 21 5 17 9" />
      <path d="M3 11V9a4 4 0 0 1 4-4h14" />
      <polyline points="7 23 3 19 7 15" />
      <path d="M21 13v2a4 4 0 0 1-4 4H3" />
    </svg>
  ),
};

// ── Sidebar items ────────────────────────────────────────────────────────────
const sidebarItems = [
  {
    name: "Edit Investor",
    icon: icons.EditInvestor,
    description: "View and update investor profiles",
    component: FarmerEdit,
  },
  {
    name: "Near Complete",
    icon: icons.NearComplete,
    description: "Investments approaching maturity",
    component: NearCompete,
  },
  {
    name: "Complete Investment",
    icon: icons.CompleteInvestment,
    description: "Finalise and close investments",
    component: FarmerFninsh,
  },
  {
    name: "Manage Investment",
    icon: icons.ManageInvestment,
    description: "Payment tracking and history",
    component: PaymentSettings,
  },
  {
    name: "Transfer Investment",
    icon: icons.TransferInvestment,
    description: "Move funds between accounts",
    component: MoneyTransf,
  },
];

// ── Component ────────────────────────────────────────────────────────────────
export default function FarmerSettings() {
  const [activeSection, setActiveSection] = useState(null); // mobile
  const [desktopSection, setDesktopSection] = useState("Edit Investor"); // desktop

  const renderActiveComponent = (section) => {
    const item = sidebarItems.find((i) => i.name === section);
    const Component = item?.component || FarmerEdit;
    return <Component />;
  };

  const activeItem = sidebarItems.find((i) => i.name === activeSection);

  return (
    <>
      {/* ════════════════════════════════════════════
          DESKTOP  (md +)  — original layout + icons
      ════════════════════════════════════════════ */}
      <motion.div
        className="flex-row hidden min-h-screen md:flex"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Sidebar */}
        <motion.div
          className="p-2 mt-5 border-r border-gray-300 w-60 dark:border-gray-500"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <motion.h1
            className="px-1 mb-4 text-2xl font-medium text-left text-gray-800 dark:text-gray-300"
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            Settings
          </motion.h1>

          <nav className="flex flex-col space-y-1">
            {sidebarItems.map((item, index) => {
              const isActive = desktopSection === item.name;
              return (
                <motion.div
                  key={item.name}
                  className={`flex items-center gap-3 px-3 py-2 text-sm rounded-lg cursor-pointer transition-all duration-200 relative overflow-hidden ${
                    isActive
                      ? "bg-green-600 text-white shadow-md"
                      : "text-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 hover:text-green-600 hover:bg-gray-100"
                  }`}
                  onClick={() => setDesktopSection(item.name)}
                  initial={{ x: -50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.4, delay: 0.1 * index }}
                  whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isActive && (
                    <motion.div
                      className="absolute inset-0 bg-green-600 rounded-lg"
                      layoutId="activeTabDesktop"
                      initial={false}
                      transition={{
                        type: "spring",
                        stiffness: 500,
                        damping: 30,
                      }}
                    />
                  )}
                  <span className="relative z-10 flex-shrink-0">
                    {item.icon}
                  </span>
                  <span className="relative z-10 font-medium">{item.name}</span>
                </motion.div>
              );
            })}
          </nav>
        </motion.div>

        {/* Main content */}
        <motion.div
          className="flex-1 p-4"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={desktopSection}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {renderActiveComponent(desktopSection)}
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </motion.div>

      {/* ════════════════════════════════════════════
          MOBILE  (below md)  — app-style nav
      ════════════════════════════════════════════ */}
      <div className="flex flex-col min-h-screen md:hidden ">
        <AnimatePresence mode="wait">
          {/* ── NAV MENU ── */}
          {activeSection === null && (
            <motion.div
              key="mobile-nav"
              className="flex flex-col flex-1"
              initial={{ opacity: 0, x: -24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.25 }}
            >
              {/* Header */}
              <div className="px-5 pt-10 pb-6">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                  Settings
                </h1>
              </div>

              {/* Nav cards */}
              <div className="flex-1 px-4 pb-8 space-y-2">
                {sidebarItems.map((item, index) => (
                  <motion.button
                    key={item.name}
                    onClick={() => setActiveSection(item.name)}
                    className="flex items-center w-full gap-4 px-4 py-4 text-left transition-all duration-150 bg-white border border-gray-100 shadow-sm rounded-2xl dark:bg-gray-900 dark:border-gray-800 active:shadow-none"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.28, delay: 0.05 * index }}
                    whileTap={{ scale: 0.97 }}
                  >
                    {/* Icon bubble */}
                    <span className="flex items-center justify-center flex-shrink-0 text-green-600 w-11 h-11 rounded-xl bg-green-50 dark:bg-green-900/30 dark:text-green-400">
                      {item.icon}
                    </span>

                    {/* Label + description */}
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold text-gray-800 dark:text-gray-100">
                        {item.name}
                      </span>
                      <span className="block text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">
                        {item.description}
                      </span>
                    </span>

                    {/* Chevron */}
                    <svg
                      className="flex-shrink-0 w-4 h-4 text-gray-300 dark:text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {/* ── CONTENT VIEW ── */}
          {activeSection !== null && (
            <motion.div
              key={`mobile-content-${activeSection}`}
              className="flex flex-col flex-1"
              initial={{ opacity: 0, x: 32 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 32 }}
              transition={{ duration: 0.25 }}
            >
              {/* Top navigation bar */}
              <div className="flex items-center gap-3 mt-5 border-b border-gray-100 dark:shadow-sm dark:bg-inherit dark:border-gray-800">
                {/* Back button */}
                <button
                  onClick={() => setActiveSection(null)}
                  className="flex items-center justify-center w-8 h-8 text-black transition-colors duration-150 bg-white rounded-full dark:bg-gray-900 dark:text-gray-300 hover:bg-green-50 dark:hover:bg-green-900/30 hover:text-green-600"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>

                {/* Icon + title */}
                <span className="items-center justify-center flex-shrink-0 hidden w-8 h-8 text-green-600 bg-green-100 rounded-full md:flex dark:bg-green-900/40 dark:text-green-400">
                  <span className="flex items-center justify-center w-4 h-4">
                    {activeItem?.icon}
                  </span>
                </span>

                <span className="flex-1 hidden text-sm font-semibold text-gray-800 truncate dark:text-gray-100">
                  {activeSection}
                </span>
              </div>

              {/* Page content */}
              <div className="flex-1">
                {renderActiveComponent(activeSection)}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}
